import { Box, styled, Typography } from '@mui/material';
import { vec2 } from 'gl-matrix';
import { ChevronDown } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
    domainEventBus,
    WindowLevelDeltaEvent,
    WindowLevelSetEvent,
} from '~/domain/events';
import { Image3D } from '~/domain/patient/image3d';
import { Modality } from '~/domain/dicom/modality';
import {
    WindowLevel,
    WindowingPreset,
    buildPresetOptions,
    findMatchingPreset,
} from '~/domain/patient/windowLevel';
import { useWindowLevelSubscription } from '~/hooks/useWindowLevelSubscription';
import { OverlayLabel } from '../../OverlayLabel';
import WindowingPresetMenu from './WindowingPresetMenu';
import {
    formatPetNumericValue,
    getPetUnitLabel,
} from '~/domain/dicom/unitSuffix';
import { PetSeriesMetadata } from '~/domain/patient/petSuv';
import SuvUnitTooltip from '~/components/Tooltips/SuvUnitTooltip';
import EditableNumericValue from '~/components/Overlay/EditableNumericValue';

const DraggableValue = styled(OverlayLabel)`
    cursor: ns-resize;
    user-select: none;
    min-width: 32px;

    &:hover {
        text-decoration: underline;
    }
`;

interface EditableWindowLevelValueProps {
    value: number;
    type: 'window' | 'center';
    seriesUid: string;
    windowLevel: WindowLevel;
    isDragging: boolean;
    onDragStart: () => void;
    onDragEnd: () => void;
}

/**
 * Window-level numeric field that supports both:
 * - vertical-drag delta adjustment with pointer lock (windowing-specific), and
 * - click-to-edit numeric input (delegated to `EditableNumericValue`).
 *
 * The drag-vs-click discriminator is the `hasDraggedRef` movement check on
 * mouseup; if no movement >=2 px occurred, we enter edit mode.
 */
const EditableWindowLevelValue = ({
    value,
    type,
    seriesUid,
    windowLevel,
    isDragging,
    onDragStart,
    onDragEnd,
}: EditableWindowLevelValueProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const hasDraggedRef = useRef(false);

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (isEditing) {
                return;
            }
            e.preventDefault();
            onDragStart();
            hasDraggedRef.current = false;

            const target = e.currentTarget as HTMLElement;
            target.requestPointerLock?.();

            const handleMouseMove = (moveEvent: MouseEvent) => {
                const deltaY = moveEvent.movementY;

                if (Math.abs(deltaY) < 2) {
                    return;
                }

                hasDraggedRef.current = true;

                const delta = vec2.create();
                if (type === 'window') {
                    delta[0] = deltaY < 0 ? 3 : -3;
                } else {
                    delta[1] = deltaY < 0 ? 3 : -3;
                }

                domainEventBus.publishSync(
                    new WindowLevelDeltaEvent(delta, seriesUid, 'user')
                );
            };

            const handleMouseUp = () => {
                onDragEnd();
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.exitPointerLock?.();

                if (!hasDraggedRef.current) {
                    setIsEditing(true);
                }
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [type, seriesUid, isEditing, onDragStart, onDragEnd]
    );

    const handleCommit = (newValue: number): void => {
        const newWindowLevel =
            type === 'window'
                ? new WindowLevel(
                      newValue,
                      windowLevel.level,
                      windowLevel.minLevel,
                      windowLevel.maxLevel
                  )
                : new WindowLevel(
                      windowLevel.window,
                      newValue,
                      windowLevel.minLevel,
                      windowLevel.maxLevel
                  );

        domainEventBus.publishSync(
            new WindowLevelSetEvent(newWindowLevel, seriesUid, 'user')
        );
    };

    // WW/WC are integer-only by clinical convention. We also reject values
    // outside the active series' allowed range so a too-large WC (which would
    // throw inside the WindowLevel value object and leave the input stuck) or
    // a non-positive WW is reverted to the prior value instead of silently
    // clamped — surfaces the rejection per the SaMD safety rules.
    const windowMax = windowLevel.maxLevel - windowLevel.minLevel;
    const validateWindow = (n: number): boolean =>
        Number.isInteger(n) && n >= 1 && n <= windowMax;
    const validateCenter = (n: number): boolean =>
        Number.isInteger(n) &&
        n >= windowLevel.minLevel &&
        n <= windowLevel.maxLevel;

    if (isEditing) {
        const isWindow = type === 'window';
        return (
            <EditableNumericValue
                value={value}
                onCommit={handleCommit}
                validate={isWindow ? validateWindow : validateCenter}
                min={isWindow ? 1 : windowLevel.minLevel}
                max={isWindow ? windowMax : windowLevel.maxLevel}
                step={1}
                onEditEnd={() => setIsEditing(false)}
                defaultEditing
            />
        );
    }

    return (
        <DraggableValue
            onMouseDown={handleMouseDown}
            sx={{
                opacity: isDragging ? 0.7 : 1,
                fontWeight: isDragging ? 'bold' : 'normal',
            }}
        >
            {value}
        </DraggableValue>
    );
};

interface WindowingControlsProps {
    image: Image3D;
    color: string;
}

const WindowingControls = ({ image, color }: WindowingControlsProps) => {
    const [presetAnchorEl, setPresetAnchorEl] = useState<HTMLElement | null>(
        null
    );
    const [isDragging, setIsDragging] = useState(false);
    const windowLevel = useWindowLevelSubscription(image);
    const seriesUid = image.seriesUid;
    const defaultWindowLevel = useMemo(
        () =>
            image.getAcquisitionWindowLevel() ??
            image.getAutoWindowLevel() ??
            image.getDefaultWindowLevel(),
        [image]
    );

    const isPetSeries = image.firstSlice.modality === Modality.PT;
    const suvFactor = isPetSeries ? image.suvFactor : null;

    const petMetadata = useMemo<PetSeriesMetadata | null>(
        () => (isPetSeries ? (image.firstSlice?.petMetadata ?? null) : null),
        [isPetSeries, image]
    );

    const presetOptions = useMemo<WindowingPreset[]>(
        () => buildPresetOptions(image),
        [image]
    );

    const formatWLValue = (rawValue: number): string => {
        if (!isPetSeries) {
            return `${rawValue}`;
        }
        return formatPetNumericValue(rawValue, suvFactor);
    };

    const wlUnitLabel = useMemo(() => {
        if (!isPetSeries) {
            return '';
        }
        const referenceValue = Math.max(
            Math.abs(windowLevel?.window ?? 0),
            Math.abs(windowLevel?.level ?? 0)
        );
        return getPetUnitLabel(suvFactor, referenceValue);
    }, [suvFactor, isPetSeries, windowLevel]);

    const handleDragStart = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
        setPresetAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => {
        setPresetAnchorEl(null);
    };

    const handleReset = () => {
        domainEventBus.publishSync(
            new WindowLevelSetEvent(defaultWindowLevel, seriesUid, 'user')
        );
    };

    if (!windowLevel) {
        return null;
    }

    const currentPreset = findMatchingPreset(presetOptions, windowLevel);

    const unitElement = wlUnitLabel ? (
        <Typography
            component="span"
            fontSize={'inherit'}
            sx={{ cursor: 'help', ml: 0.25 }}
        >
            {'('}
            <SuvUnitTooltip metadata={petMetadata}>
                {wlUnitLabel}
            </SuvUnitTooltip>
            {')'}
        </Typography>
    ) : null;

    return (
        <Box
            display="flex"
            gap={1}
            data-testid={`windowing-controls-${color === 'primary.main' ? 'primary' : 'secondary'}`}
        >
            <Box display="flex" gap={0.5}>
                <OverlayLabel color={color}>WW{unitElement}:</OverlayLabel>

                {isPetSeries ? (
                    <OverlayLabel>
                        {formatWLValue(windowLevel.window)}
                    </OverlayLabel>
                ) : (
                    <EditableWindowLevelValue
                        value={windowLevel.window}
                        type="window"
                        seriesUid={seriesUid}
                        windowLevel={windowLevel}
                        isDragging={isDragging}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    />
                )}
            </Box>
            <Box display="flex" gap={0.5}>
                <OverlayLabel color={color}>WC{unitElement}:</OverlayLabel>
                {isPetSeries ? (
                    <OverlayLabel>
                        {formatWLValue(windowLevel.level)}
                    </OverlayLabel>
                ) : (
                    <EditableWindowLevelValue
                        value={windowLevel.level}
                        type="center"
                        seriesUid={seriesUid}
                        windowLevel={windowLevel}
                        isDragging={isDragging}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    />
                )}
            </Box>
            {!isPetSeries && (
                <Box display="flex" alignItems="center" gap={2} ml={0.5}>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.25}
                        onClick={handleMenuOpen}
                        data-testid="windowing-preset-selector"
                        sx={{
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.8 },
                        }}
                    >
                        <OverlayLabel sx={{ whiteSpace: 'nowrap' }}>
                            {currentPreset?.name ?? 'Preset'}
                        </OverlayLabel>
                        <ChevronDown size={9} style={{ marginTop: 2 }} />
                    </Box>
                    <WindowingPresetMenu
                        anchorEl={presetAnchorEl}
                        onClose={handleMenuClose}
                        image={image}
                        windowLevel={windowLevel}
                    />

                    <OverlayLabel
                        onClick={handleReset}
                        sx={{
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Reset
                    </OverlayLabel>
                </Box>
            )}
        </Box>
    );
};

export default WindowingControls;
